import { useState, useEffect, useCallback, useMemo } from 'react';
import courseCategoryService, {fetchExpertSkills} from '../Services/ExpertCourseCategoryService';
import { toast } from 'react-toastify';

const useCourseCategories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [skills, setSkills] = useState(null);
    const [skillsLoading, setSkillsLoading] = useState(true);
    const [dataInitialized, setDataInitialized] = useState(false);
    
    // Pagination state
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0
    });

    // Memoized Main Categories - only those with subCategories
    const mainCategories = useMemo(() => {
        return categories.filter(category =>
            category.isMainCategory &&
            category.parentCategoryId === null &&
            category.subCategories.length > 0
        );
    }, [categories]);

    // Fetch categories from API
    const fetchCategories = useCallback(async (
        onlyWithSubCategories = true, 
        page = 1, 
        pageSize = 10,
        append = false
    ) => {
        try {
            if (page === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            
            setError(null);

            const response = await courseCategoryService.getCourseCategories(
                onlyWithSubCategories,
                page,
                pageSize
            );
            
            console.log("Categories response:", response);

            if (append) {
                // Append new categories to existing ones
                setCategories(prev => [...prev, ...(response.items || [])]);
            } else {
                // Replace categories with new ones
                setCategories(response.items || []);
            }
            
            // Update pagination state
            setPagination({
                currentPage: response.pageNumber || page,
                pageSize: response.pageSize || pageSize,
                totalCount: response.totalCount || 0,
                totalPages: response.totalPages || 0
            });

            return response.items || [];
        } catch (err) {
            setError(err.message || 'Failed to fetch categories');
            throw err;
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    // Fetch more categories
    const fetchMoreCategories = useCallback(async () => {
        if (loadingMore || pagination.currentPage >= pagination.totalPages) return;
        
        try {
            await fetchCategories(
                true, 
                pagination.currentPage + 1, 
                pagination.pageSize,
                true // append to existing categories
            );
        } catch (err) {
            console.error('Error fetching more categories:', err);
        }
    }, [fetchCategories, loadingMore, pagination]);

    // Check if there are more categories to load
    const hasMoreCategories = useMemo(() => {
        return pagination.currentPage * pagination.pageSize < pagination.totalCount;
    }, [pagination]);

    // Fetch user's existing skills
    const fetchUserSkills = useCallback(async () => {
        try {
            setSkillsLoading(true);
            const data = await fetchExpertSkills();
            console.log("Expert Skills:", data);
            setSkills(data);
            return data;
        } catch (err) {
            console.error('Error fetching Expert Skills:', err);
            return null;
        } finally {
            setSkillsLoading(false);
        }
    }, []);

    // Pre-select categories based on user skills
    const preSelectCategories = useCallback((categoriesData, skillsData) => {
        if (!skillsData || skillsData.length === 0 || !categoriesData || categoriesData.length === 0) {
            return [];
        }

        // Extract all subcategory IDs from the skills data
        const subCategoryIds = [];
        skillsData.forEach(category => {
            if (category.subCategories && category.subCategories.length > 0) {
                category.subCategories.forEach(sub => {
                    subCategoryIds.push(sub.id);
                });
            }
        });

        // Find the full subcategory objects from the categories list
        const preSelectedCategories = [];
        categoriesData.forEach(category => {
            if (category.subCategories && category.subCategories.length > 0) {
                category.subCategories.forEach(sub => {
                    if (subCategoryIds.includes(sub.id)) {
                        preSelectedCategories.push(sub);
                    }
                });
            }
        });

        return preSelectedCategories;
    }, []);

    // Initialize all data
    const initializeData = useCallback(async () => {
        try {
            setLoading(true);
            setSkillsLoading(true);
            setDataInitialized(false);

            // Fetch categories first
            const categoriesData = await fetchCategories(true, 1, 10, false);

            // Then fetch user skills
            const expertSkills = await fetchUserSkills();

            // Pre-select categories based on skills
            const preSelected = preSelectCategories(categoriesData, expertSkills);
            setSelectedCategories(preSelected);

            setDataInitialized(true);
        } catch (err) {
            console.error('Error initializing data:', err);
            setError(err.message || 'Failed to initialize data');
        } finally {
            setLoading(false);
            setSkillsLoading(false);
        }
    }, [fetchCategories, fetchUserSkills, preSelectCategories]);

    // Toggle category selection with validation
    const toggleCategorySelection = useCallback((category) => {
        setSelectedCategories(prev => {
            const isSelected = prev.some(item => item.id === category.id);

            // If trying to remove
            if (isSelected) {
                return prev.filter(item => item.id !== category.id);
            }
            // If trying to add - check maximum limit (15)
            else if (prev.length >= 15) {
                alert('Maximum 15 skills can be selected. Please remove some selections to add new ones.');
                return prev;
            }
            else {
                return [...prev, category];
            }
        });
    }, []);

    
    // Submit selections to API
    const submitSelections = useCallback(async () => {
        // Validation: Minimum 1 selection required
        if (selectedCategories.length < 1) {
            setSubmitError(`Minimum 1 skill required. You have selected ${selectedCategories.length}.`);
            return null;
        }

        // Get subCategoryIds from selected categories
        const subCategoryIds = selectedCategories.map(cat => cat.id);

        try {
            setIsSubmitting(true);
            setSubmitError(null);

            const response = await courseCategoryService.createExpertSkills(subCategoryIds);
            setSubmitSuccess(true);
            console.log('Expert Skills created successfully:', response);
            toast.success('Expert Skills created/updated successfully');

            // Update the skills state with the new selections
            const newSkills = mainCategories.map(category => {
                const selectedSubCategories = category.subCategories.filter(sub =>
                    subCategoryIds.includes(sub.id)
                );

                if (selectedSubCategories.length > 0) {
                    return {
                        ...category,
                        subCategories: selectedSubCategories
                    };
                }
                return null;
            }).filter(Boolean);

            setSkills(newSkills);

            return {
                success: true,
                data: response,
                selectedCount: selectedCategories.length,
                selectedCategories: selectedCategories
            };
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to save Skills';
            setSubmitError(errorMessage);
            console.error('Error submitting selections:', err);
            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedCategories, mainCategories]);

    // Initialize on mount
    useEffect(() => {
        if (!dataInitialized) {
            initializeData();
        }
    }, [dataInitialized, initializeData]);

    return {
        categories,
        mainCategories,
        loading,
        loadingMore,
        error,
        selectedCategories,
        isSubmitting,
        submitError,
        submitSuccess,
        skills,
        skillsLoading,
        pagination,
        hasMoreCategories,
        fetchMoreCategories,
        toggleCategorySelection,
        submitSelections,
        refetch: initializeData
    };
};

export default useCourseCategories;